import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

export default function AuctionInsights({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const primaryColor = brand.colors?.primary || '#2563eb';

  useEffect(() => {
    setLoading(true);
    fetcher.fetchAuctionInsights(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.competitors?.length) return null;

  const pct = (v) => `${(v * 100).toFixed(0)}%`;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Competitor Auction Insights
      </h3>
      <p className="text-xs text-gray-400 mb-4">How you compare against competitors in search auctions</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
              <th className="text-left py-2 pr-4 font-medium">Domain</th>
              <th className="text-right py-2 px-2 font-medium">Impr. Share</th>
              <th className="text-right py-2 px-2 font-medium">Overlap</th>
              <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">Above You</th>
              <th className="text-right py-2 pl-2 font-medium hidden sm:table-cell">Outranking</th>
            </tr>
          </thead>
          <tbody>
            {/* Your row */}
            {data.you && (
              <tr className="border-b border-gray-50" style={{ backgroundColor: `${primaryColor}08` }}>
                <td className="py-2.5 pr-4">
                  <span className="font-semibold" style={{ color: primaryColor }}>You</span>
                </td>
                <td className="text-right py-2.5 px-2">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${data.you.impressionShare * 100}%`, backgroundColor: primaryColor }}
                      />
                    </div>
                    <span className="font-medium" style={{ color: primaryColor }}>
                      {pct(data.you.impressionShare)}
                    </span>
                  </div>
                </td>
                <td className="text-right py-2.5 px-2 text-gray-400">—</td>
                <td className="text-right py-2.5 px-2 text-gray-400 hidden sm:table-cell">—</td>
                <td className="text-right py-2.5 pl-2 text-gray-400 hidden sm:table-cell">—</td>
              </tr>
            )}

            {/* Competitor rows */}
            {data.competitors.map((c, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="py-2.5 pr-4 text-gray-700 truncate max-w-[160px]">{c.domain}</td>
                <td className="text-right py-2.5 px-2">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="h-full rounded-full bg-gray-400"
                        style={{ width: `${c.impressionShare * 100}%` }}
                      />
                    </div>
                    <span className="text-gray-600">{pct(c.impressionShare)}</span>
                  </div>
                </td>
                <td className="text-right py-2.5 px-2 text-gray-600">{pct(c.overlapRate)}</td>
                <td className="text-right py-2.5 px-2 text-gray-600 hidden sm:table-cell">{pct(c.positionAboveRate)}</td>
                <td className="text-right py-2.5 pl-2 text-gray-600 hidden sm:table-cell">{pct(c.outrankingShare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
